import "../styles/schedule-base-alert-modal.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatDateLongScheduled, parseDate } from "../utilities/utilities";
import {
  SCHEDULE_DAYS_OF_WEEK,
  SCHEDULE_HOUR_OPTIONS,
  SCHEDULE_MERIDIEM_OPTIONS,
  SCHEDULE_MINUTE_OPTIONS,
  SCHEDULE_REPEAT_PERIOD_OPTIONS,
  buildScheduleRecurringPatternKey,
  formatScheduleCardDate,
  formatScheduleCardTime,
  formatScheduleDateKey,
  formatScheduleShortDate,
  generateScheduleRecurringDates,
  getScheduleTimeZoneLabel,
  getScheduleTimezone,
  isFutureScheduleDateTime,
  mergeScheduleDateWithTime,
  normalizeScheduleDateValue,
  toScheduleStartOfDay,
} from "../utilities/constants";
import { Modal } from "./modal";
import { GuiButton } from "./global-ui/gui-button";
import GuiCustomDatepickerDropdown from "./global-ui/gui-custom-datepicker-dropdown";
import { GuiSelect } from "./global-ui/gui-select";
import GuiCalendar from "./global-ui/gui-calendar";

export default function ScheduleBaseAlertModal({ value, onSave, onCancel }) {
  const initialScheduledDates = useMemo(() => {
    const values = Array.isArray(value) ? value : value ? [value] : [];

    return values
      .map((entry) => normalizeScheduleDateValue(entry))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());
  }, [value]);

  const patternIdRef = useRef(initialScheduledDates.length + 1);

  const createEntryId = () => {
    const id = patternIdRef.current;
    patternIdRef.current += 1;
    return id;
  };

  const initialEntries = useMemo(
    () =>
      initialScheduledDates.map((entry, index) => ({
        id: index + 1,
        type: "single",
        date: entry,
      })),
    [initialScheduledDates],
  );

  const [mode, setMode] = useState("single");
  const [pickerDate, setPickerDate] = useState(null);
  const [hour, setHour] = useState("9");
  const [minute, setMinute] = useState("00");
  const [period, setPeriod] = useState("AM");
  const [error, setError] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState(initialEntries);
  const [repeatEvery, setRepeatEvery] = useState("1");
  const [repeatPeriod, setRepeatPeriod] = useState("weeks");
  const [repeatOnDays, setRepeatOnDays] = useState([]);
  const [endType, setEndType] = useState("on");
  const [endOnDate, setEndOnDate] = useState(null);
  const [afterOccurrences, setAfterOccurrences] = useState("3");
  const modalContentRef = useRef(null);

  const timezone = useMemo(() => getScheduleTimezone(), []);
  const timeZoneLabel = useMemo(
    () => getScheduleTimeZoneLabel(timezone),
    [timezone],
  );

  const hourOptions = useMemo(
    () =>
      SCHEDULE_HOUR_OPTIONS.map((entry) => ({
        value: entry,
        label: entry,
      })),
    [],
  );

  const minuteOptions = useMemo(
    () =>
      SCHEDULE_MINUTE_OPTIONS.map((entry) => ({
        value: entry,
        label: entry,
      })),
    [],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      modalContentRef.current?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCancel]);

  const selectedDateTime = useMemo(() => {
    if (!pickerDate) return null;
    return mergeScheduleDateWithTime(pickerDate, hour, minute, period);
  }, [pickerDate, hour, minute, period]);

  const allSelectedDates = useMemo(
    () =>
      selectedEntries
        .flatMap((entry) =>
          entry.type === "single" ? [entry.date] : entry.occurrences,
        )
        .sort((a, b) => a.getTime() - b.getTime()),
    [selectedEntries],
  );

  const selectedDateKeys = useMemo(
    () =>
      new Set(allSelectedDates.map((entry) => formatScheduleDateKey(entry))),
    [allSelectedDates],
  );

  const canAddSingle =
    !!selectedDateTime && isFutureScheduleDateTime(selectedDateTime);

  const recurringCanAdd = useMemo(() => {
    if (!pickerDate || !isFutureScheduleDateTime(selectedDateTime))
      return false;
    if (repeatPeriod === "weeks" && !repeatOnDays.length) return false;

    const repeatValue = parseInt(repeatEvery, 10);
    if (!repeatValue || repeatValue < 1) return false;

    if (endType === "on") {
      if (!endOnDate) return false;
      return (
        toScheduleStartOfDay(endOnDate).getTime() >=
        toScheduleStartOfDay(pickerDate).getTime()
      );
    }

    const occurrencesValue = parseInt(afterOccurrences, 10);
    return !!occurrencesValue && occurrencesValue > 0;
  }, [
    selectedDateTime,
    pickerDate,
    repeatOnDays,
    repeatPeriod,
    repeatEvery,
    endType,
    endOnDate,
    afterOccurrences,
  ]);

  function updateDate(name, value) {
    if (name !== "date") return;

    const parsedDate = parseDate(value);
    if (!parsedDate) return;

    setPickerDate(parsedDate);

    if (
      mode === "recurring" &&
      repeatPeriod === "weeks" &&
      !repeatOnDays.length
    ) {
      setRepeatOnDays([parsedDate.getDay()]);
    }

    if (mode === "recurring" && !endOnDate) {
      setEndOnDate(parsedDate);
    }

    setError(null);
  }

  function addSingleDateTime() {
    if (!selectedDateTime) {
      setError("Please select a date and time.");
      return;
    }

    if (!isFutureScheduleDateTime(selectedDateTime)) {
      setError("Please select a future time. Past times are not allowed.");
      return;
    }

    const dateKey = formatScheduleDateKey(selectedDateTime);
    if (selectedDateKeys.has(dateKey)) {
      setError("That date and time has already been added.");
      return;
    }

    setSelectedEntries((current) => [
      ...current,
      {
        id: createEntryId(),
        type: "single",
        date: selectedDateTime,
      },
    ]);
    setError(null);
  }

  function toggleRepeatDay(dayId) {
    setRepeatOnDays((current) => {
      if (current.includes(dayId)) {
        return current.filter((item) => item !== dayId);
      }
      return [...current, dayId].sort((a, b) => a - b);
    });
    setError(null);
  }

  function addRecurringPattern() {
    if (!recurringCanAdd) {
      setError("Please complete recurring settings before adding the pattern.");
      return;
    }

    const recurringDates = generateScheduleRecurringDates({
      startDate: pickerDate,
      hour,
      minute,
      period,
      repeatEvery,
      repeatPeriod,
      repeatOnDays,
      endType,
      endOnDate,
      afterOccurrences,
    });

    if (!recurringDates.length) {
      setError("No valid future dates found for this recurring pattern.");
      return;
    }

    const patternKey = buildScheduleRecurringPatternKey({
      startDate: pickerDate,
      hour,
      minute,
      period,
      repeatEvery,
      repeatPeriod,
      repeatOnDays,
      endType,
      endOnDate,
      afterOccurrences,
    });

    if (
      selectedEntries.some(
        (entry) =>
          entry.type === "recurring" && entry.patternKey === patternKey,
      )
    ) {
      setError("That recurring pattern has already been added.");
      return;
    }

    const hasAnyDuplicateDate = recurringDates.some((entry) =>
      selectedDateKeys.has(formatScheduleDateKey(entry)),
    );
    if (hasAnyDuplicateDate) {
      setError("One or more recurring dates already exist in your selection.");
      return;
    }

    const daySummary = [...repeatOnDays]
      .sort((a, b) => a - b)
      .map(
        (entry) => SCHEDULE_DAYS_OF_WEEK.find((day) => day.id === entry)?.label,
      )
      .filter(Boolean)
      .join(", ");

    const summaryByPeriod = {
      days: `Every ${repeatEvery} day(s)`,
      weeks: `Weekly (${daySummary})`,
      months: `Every ${repeatEvery} month(s)`,
      years: `Every ${repeatEvery} year(s)`,
    };

    setSelectedEntries((current) => [
      ...current,
      {
        id: createEntryId(),
        type: "recurring",
        patternKey,
        occurrences: recurringDates,
        summary: summaryByPeriod[repeatPeriod] || "Recurring",
      },
    ]);
    setError(null);
  }

  function removeSchedule(entryId) {
    setSelectedEntries((current) =>
      current.filter((item) => item.id !== entryId),
    );
  }

  function clearAll() {
    setSelectedEntries([]);
    setError(null);
  }

  function handleSave() {
    const seen = new Set();
    const uniqueDates = [];

    allSelectedDates.forEach((entry) => {
      const key = formatScheduleDateKey(entry);
      if (seen.has(key)) return;
      seen.add(key);
      uniqueDates.push(entry);
    });

    onSave(uniqueDates);
  }

  function switchToSingleMode() {
    setMode("single");
    setError(null);
  }

  function switchToRecurringMode() {
    setMode("recurring");
    if (pickerDate && repeatPeriod === "weeks" && !repeatOnDays.length) {
      setRepeatOnDays([pickerDate.getDay()]);
    }
    if (pickerDate && !endOnDate) {
      setEndOnDate(pickerDate);
    }
    setError(null);
  }

  return (
    <Modal className="schedule-base-alert-modal" onClose={onCancel}>
      <div
        className="schedule-base-alert-modal-content"
        role="dialog"
        aria-modal="true"
        aria-label="Schedule Message"
        tabIndex={-1}
        ref={modalContentRef}
      >
        <div className="schedule-header">
          <h3>Schedule Message</h3>
          <button
            type="button"
            aria-label="Close"
            className="close-btn"
            onClick={onCancel}
          >
            <i className="fal fa-times"></i>
          </button>
        </div>

        <p className="schedule-subtitle">
          Choose the dates and times you want to schedule this message for.
        </p>

        <div className="schedule-layout">
          <div className="schedule-left-panel">
            <div className="schedule-tabs">
              <button
                type="button"
                className={mode === "single" ? "active" : ""}
                aria-pressed={mode === "single"}
                onClick={switchToSingleMode}
              >
                Single Times
              </button>
              <button
                type="button"
                className={mode === "recurring" ? "active" : ""}
                aria-pressed={mode === "recurring"}
                onClick={switchToRecurringMode}
              >
                Recurring
              </button>
            </div>

            <h4>Select Date</h4>
            <GuiCalendar name="date" value={pickerDate} onChange={updateDate} />

            <h4>Select Time</h4>
            <div className="schedule-time-inputs">
              <GuiSelect
                className="schedule-gui-select"
                menuClassName="schedule-modal-menu"
                name="hour"
                value={hour}
                options={hourOptions}
                isSearchable={false}
                inModal
                onChange={(_, option) => {
                  setHour(option?.value || "9");
                  setError(null);
                }}
              />

              <GuiSelect
                className="schedule-gui-select"
                menuClassName="schedule-modal-menu"
                name="minute"
                value={minute}
                options={minuteOptions}
                isSearchable={false}
                inModal
                onChange={(_, option) => {
                  setMinute(option?.value || "00");
                  setError(null);
                }}
              />

              <GuiSelect
                className="schedule-gui-select"
                menuClassName="schedule-modal-menu"
                name="am-pm"
                value={period}
                options={SCHEDULE_MERIDIEM_OPTIONS}
                isSearchable={false}
                inModal
                onChange={(_, option) => {
                  setPeriod(option?.value || "AM");
                  setError(null);
                }}
              />
            </div>

            {mode === "single" && (
              <GuiButton
                className="add-date-time-btn"
                disabled={!canAddSingle}
                onClick={addSingleDateTime}
              >
                Add Date & Time
              </GuiButton>
            )}

            {mode === "recurring" && (
              <div className="recurring-controls">
                <h4>Repeat</h4>
                <div className="repeat-grid">
                  <div className="repeat-field">
                    <label>Every</label>
                    <input
                      type="number"
                      min="1"
                      value={repeatEvery}
                      onChange={(event) => {
                        setRepeatEvery(event.target.value);
                        setError(null);
                      }}
                    />
                  </div>
                  <div className="repeat-field">
                    <label>Period</label>
                    <GuiSelect
                      className="schedule-gui-select"
                      menuClassName="schedule-modal-menu"
                      name="repeat-period"
                      value={repeatPeriod}
                      options={SCHEDULE_REPEAT_PERIOD_OPTIONS}
                      isSearchable={false}
                      inModal
                      onChange={(_, option) => {
                        const nextPeriod = option?.value;
                        if (!nextPeriod) return;
                        setRepeatPeriod(nextPeriod);
                        if (
                          nextPeriod === "weeks" &&
                          pickerDate &&
                          !repeatOnDays.length
                        ) {
                          setRepeatOnDays([pickerDate.getDay()]);
                        }
                        setError(null);
                      }}
                    />
                  </div>
                </div>

                {repeatPeriod === "weeks" && (
                  <>
                    <h4>Repeat On</h4>
                    <div className="repeat-days">
                      {SCHEDULE_DAYS_OF_WEEK.map((day) => (
                        <button
                          key={day.id}
                          type="button"
                          className={
                            repeatOnDays.includes(day.id) ? "active" : ""
                          }
                          onClick={() => toggleRepeatDay(day.id)}
                        >
                          {day.short}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <h4>Ends</h4>
                <div className="ends-section">
                  <label>
                    <input
                      type="radio"
                      checked={endType === "on"}
                      onChange={() => {
                        setEndType("on");
                        setError(null);
                      }}
                    />
                    On
                  </label>
                  <GuiCustomDatepickerDropdown
                    dateLabel=""
                    dateSelected={endOnDate}
                    disabled={endType !== "on"}
                    onDateChange={(value) => {
                      const parsed = value ? new Date(value) : null;
                      setEndOnDate(parsed);
                      setError(null);
                    }}
                  />

                  <label>
                    <input
                      type="radio"
                      checked={endType === "after"}
                      onChange={() => {
                        setEndType("after");
                        setError(null);
                      }}
                    />
                    After
                  </label>
                  <div className="after-occurrences-field">
                    <input
                      type="number"
                      min="1"
                      disabled={endType !== "after"}
                      value={afterOccurrences}
                      onChange={(event) => {
                        setAfterOccurrences(event.target.value);
                        setError(null);
                      }}
                    />
                    <span className="after-occurrences-suffix">Recurrence</span>
                  </div>
                </div>

                <GuiButton
                  className="add-date-time-btn"
                  disabled={!recurringCanAdd}
                  onClick={addRecurringPattern}
                >
                  Add Recurring Pattern
                </GuiButton>
              </div>
            )}

            <span className="error-msg">{error || "\u00A0"}</span>
          </div>

          <div className="schedule-right-panel">
            <div className="selected-header-row">
              <div>
                <h4>Selected</h4>
                <p>{allSelectedDates.length} dates</p>
              </div>
              {selectedEntries.length > 0 && (
                <button type="button" className="clear-btn" onClick={clearAll}>
                  Clear
                </button>
              )}
            </div>

            {selectedEntries.length === 0 ? (
              <div className="selected-empty-state">
                <i className="fal fa-calendar-alt"></i>
                <span>No dates yet</span>
              </div>
            ) : (
              <div className="selected-cards">
                {selectedEntries.map((entry) => {
                  const firstOccurrence =
                    entry.type === "single" ? entry.date : entry.occurrences[0];

                  return (
                    <div className="selected-card" key={entry.id}>
                      <div className="selected-card-content">
                        <div className="selected-meta-row selected-date">
                          <i className="fal fa-calendar" aria-hidden="true"></i>
                          <span>
                            {entry.type === "single"
                              ? formatScheduleCardDate(firstOccurrence)
                              : formatScheduleShortDate(firstOccurrence)}
                          </span>
                        </div>
                        <div className="selected-meta-row selected-time">
                          <i className="fal fa-clock" aria-hidden="true"></i>
                          <span>
                            {formatScheduleCardTime(firstOccurrence)}{" "}
                            {timeZoneLabel}
                          </span>
                        </div>
                        {entry.type === "recurring" && (
                          <>
                            <div className="selected-meta-row selected-rule">
                              <i className="fal fa-sync" aria-hidden="true"></i>
                              <span>{entry.summary}</span>
                            </div>
                            <div className="selected-meta-row selected-count">
                              <i
                                className="fal fa-infinity"
                                aria-hidden="true"
                              ></i>
                              <span>
                                # {entry.occurrences.length} occurrences
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        aria-label={`Remove ${formatDateLongScheduled(firstOccurrence)}`}
                        className="remove-btn"
                        onClick={() => removeSchedule(entry.id)}
                      >
                        <i className="fal fa-times"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="schedule-footer">
          <GuiButton dataQa="cancel" className="btn-outline" onClick={onCancel}>
            Cancel
          </GuiButton>
          <GuiButton
            dataQa="scheduled"
            className="save-btn"
            disabled={selectedEntries.length === 0}
            onClick={handleSave}
          >
            Save
          </GuiButton>
        </div>
      </div>
    </Modal>
  );
}
